// 9fbef606107a605d69c0edbcd8029e5d
import endpoints from '@tcp/core/src/services/endpoints';
import { executeStatefulAPICall } from '@tcp/core/src/services/handler';
import { ERRORS } from '@tcp/core/src/utils/errorMessage.util';
import { getDateObject, getPlaceSiteId } from '@tcp/core/src/utils';
import { getStoreRef } from '@tcp/core/src/utils/store.utils';
import { logout } from '@tcp/core/src/components/features/account/Logout/container/LogOut.actions';
import { GRAPHQL_EXCEPTIONS } from '../../../reduxStore/constants';

const getFormattedError = (err) => {
  if (err.response && err.response.body) {
    return err.response.body.errors[0]
      ? err.response.body.errors[0].errorKey
      : err.response.body.errorKey;
  }
  return 'genericError';
};

/**
 * This method is used to extract error message from the response
 * @param {object} err - Error response object
 */
export const errorHandler = (err) => {
  if (err.response && err.response.body && err.response.body.errors) {
    throw getFormattedError(err);
  } else if (err && err.err && err.err.errorMessage) {
    // eslint-disable-next-line no-underscore-dangle
    throw err.err.errorMessage._error;
  }
  throw new Error(ERRORS.SYSTEM_ERROR);
};

/**
 * @function UpdateProfileInfo
 * @summary
 * @param {type} paramName -
 * @return TDB
 */

export const UpdateProfileInfo = (args) => {
  const {
    firstName,
    lastName,
    associateId,
    email,
    phone,
    currentPassword,
    newPassword,
    newPasswordVerify,
    status,
    userBirthday,
    airmiles,
    useMicroService = false,
    logonId = '',
    fromPLCCModal,
    isSfccPlatform = true,
    phoneCountryCode,
  } = args;

  const { updateProfileInfo: updateProfileInfoApi, changePassword, changePasswordPLCC } = endpoints;
  const selectEndpoints = useMicroService ? changePassword : updateProfileInfoApi;
  const payload = {
    webService: fromPLCCModal ? changePasswordPLCC : selectEndpoints,
    body: {
      firstName,
      lastName,
      associateId,
      email1: email,
      phone1: phone,
      currentPassword,
      logonPassword: newPassword,
      logonPasswordVerify: newPasswordVerify,
      status,
      operation: (currentPassword && newPassword && 'resetPassword') || '', // If resetPassword backend will validate currentPassword
      userBirthday,
      logonId,
      phoneCountryCode,
    },
    header: {
      siteId: getPlaceSiteId(),
      isSfccPlatform,
    },
  };

  if (airmiles) {
    payload.body.customMemberAttributes = [{ airMilesCard: airmiles }];
  }

  return executeStatefulAPICall(payload)
    .then(() => {
      return 'successMessage';
    })
    .catch((err) => {
      const { errorCode } = err.errorResponse.errors[0];
      if (GRAPHQL_EXCEPTIONS.ERROR_CODES.includes(errorCode)) {
        const storeRef = getStoreRef();
        if (storeRef) {
          storeRef.dispatch(logout());
        }
      }
      throw err;
    });
};

const applicationString = 'application/json';

/**
 * @function getChildren
 * @summary this function will make an API call to fetch children's birthday information
 */
export const getChildren = () => {
  const payload = {
    header: {
      'Content-Type': applicationString,
      Accept: applicationString,
      siteId: getPlaceSiteId(),
    },
    webService: endpoints.getChildren,
  };

  return executeStatefulAPICall(payload)
    .then((res) => {
      // We are doing parseInt(child.childBirthdayMonth).toString() beacuse we use this value to index a table and backend can send it with a leading Zero like 02
      return res.body?.data?.childDetails.map(
        ({ childName, birthYear, birthMonth, gender, childId }) => ({
          name: childName,
          birthYear,
          birthMonth: parseInt(birthMonth, 10).toString(),
          gender,
          childId,
        })
      );
    })
    .catch((err) => {
      throw err;
    });
};

/**
 * @function deleteChild
 * @summary
 * @param {type} paramName -
 * @return TDB
 */
export const deleteChild = (args) => {
  const deleteChildAPI = {
    ...endpoints.deleteChild,
    URI: `${endpoints.deleteChild.URI}/${args.childId}`,
  };
  const payload = {
    header: {
      'Content-Type': applicationString,
      Accept: applicationString,
      siteId: getPlaceSiteId(),
    },
    webService: deleteChildAPI,
  };

  return executeStatefulAPICall(payload)
    .then((res) => {
      return res.body.data;
    })
    .catch((err) => {
      throw err;
    });
};

/**
 * @function add child birthday
 * @summary
 * @param {type} paramName -
 * @return TDB
 */
export const addChildBirthday = (args) => {
  const d = getDateObject();
  const payload = {
    header: {
      'Content-Type': applicationString,
      Accept: applicationString,
      siteId: getPlaceSiteId(),
    },
    body: {
      firstName: args.firstName,
      lastName: args.lastName,
      timestamp: `${d.getFullYear()}-${d.getMonth()}-${d.getDate()} ${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}.${d.getMilliseconds()}`,
      childName: args.childName,
      birthMonth: args.childBirthMonth,
      birthYear: args.childBirthYear,
      gender: args.gender === '0' ? '02' : '01',
    },
    webService: endpoints.addChild,
  };

  return executeStatefulAPICall(payload)
    .then((res) => res)
    .catch((err) => {
      throw err;
    });
};

/**
 * This is a service method to post survey data.
 * @param {object} args - request payload containing answer1, answer2 values to submit in the backend.
 */
export const submitUserSurvey = (args) => {
  const payload = {
    header: {
      'Content-Type': applicationString,
      accept: '*/*',
      siteId: getPlaceSiteId(),
    },
    body: {
      ...args.payload,
    },
    webService: endpoints.updateUserSurvey,
  };

  return executeStatefulAPICall(payload)
    .then((res) => {
      const resObj = res.body.data;
      const answers = [];
      answers[0] = resObj.answer1 ? resObj.answer1.split('|') : [];
      answers[1] = resObj.answer2 ? resObj.answer2.split('|') : [];
      return {
        ...answers,
        success: true,
      };
    })
    .catch((err) => {
      throw err;
    });
};
